/**
 * RU-UIMOBILE UM-1: Indian money formatting for display.
 *
 * formatInrCompact: ₹99,999.00 · ₹1.00L · ₹6.05L · ₹5.45Cr · ₹-1.05L (minus right after ₹, the Cockpit's
 * convention). formatInrFull: the exact value, ₹-1,05,443.60. Neither changes the number it is given.
 */
export const LAKH = 1e5;
export const CRORE = 1e7;

// RU-UIFU UF-3: only a number or a numeric string counts. A blank / whitespace-only string, an array, an object or
// a boolean is not a number (Number(" ") and Number([]) are 0, Number([5]) is 5).
function toNumber(v) {
  let n;
  if (typeof v === "number") n = v;
  else if (typeof v === "string" && v.trim() !== "") n = Number(v);
  else return null;
  return Number.isFinite(n) ? n : null;
}

function grouped(abs, decimals) {
  return abs.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function signOf(n, plus) {
  if (n < 0) return "-";
  return plus && n > 0 ? "+" : "";
}

/** Exact value: Indian grouping, `decimals` (default 2) fraction digits. "—" when not a number. */
export function formatInrFull(v, { plus = false, decimals = 2 } = {}) {
  const n = toNumber(v);
  if (n === null) return "—";
  const abs = Math.abs(n);
  const body = grouped(abs, decimals);
  // A value that rounds to zero at this precision carries no sign.
  const sign = /[1-9]/.test(body) ? signOf(n, plus) : "";
  return `₹${sign}${body}`;
}

// "12.34" from 1234 hundredths (an integer), so no float formatting is involved.
function hundredths(units) {
  return `${Math.floor(units / 100)}.${String(units % 100).padStart(2, "0")}`;
}

/** Compact value: plain below 1 lakh, then L (lakh) and Cr (crore) with 2 decimals. */
export function formatInrCompact(v, { plus = false, decimals = 2 } = {}) {
  const n = toNumber(v);
  if (n === null) return "—";
  const abs = Math.abs(n);
  if (abs < LAKH) {
    // RU-UIFU UF-1: the tier is decided after rounding — a value whose plain text rounds to 1,00,000 reads in lakhs.
    const full = formatInrFull(n, { plus, decimals });
    if (Number(grouped(abs, decimals).replace(/,/g, "")) < LAKH) return full;
  }
  // RU-UIFU UF-2: one division by ₹1,000 (hundredths of a lakh) / ₹1,00,000 (hundredths of a crore), then half away
  // from zero on |v|. An exact tie (…500 / …50,000 rupees) is an integer, so the quotient's .5 is exact; never
  // `v / 1e5 * 100` (1,00,500 → 100.4999…).
  let body;
  const lakhUnits = Math.round(abs / 1e3);
  if (abs < CRORE && lakhUnits < 10000) {
    body = `${hundredths(lakhUnits)}L`;
  } else {
    const croreUnits = Math.round(abs / 1e5);
    body = `${grouped(croreUnits / 100, 2)}Cr`;
  }
  return `₹${signOf(n, plus)}${body}`;
}
